import { useState } from "preact/compat"
import { CandsConfig, Integration, IntegrationWithJobInfo, updateIntegration } from "@/api/integration"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast"
import { EmailSelector } from "@/setup/config/email-config-page"

interface CandsUcdClaimsFormProps {
  integration: Integration
  emailAccounts: IntegrationWithJobInfo[]
  onSuccess: (int: Integration) => void
}

/**
 * C&S rejects early-pay disputes filed in the VCM portal and asks vendors to email UCD claims
 * instead, so these settings drive the batched claim file rather than a portal submission.
 */
export function CandsUcdClaimsForm({ integration, emailAccounts, onSuccess }: CandsUcdClaimsFormProps) {
  const [loading, setLoading] = useState(false)
  const data = integration.data as unknown as CandsConfig
  const [selected_email, set_selected_email] = useState(data.dispute_from_email ?? "")

  const form = useFormState({
    dispute_from_name: data.dispute_from_name ?? "",
    vendor_number: data.vendor_number ?? "",
    discount_term: data.discount_term?.toString() ?? "",
    discount_percentage: data.discount_percentage?.toString() ?? "",
  })

  async function on_submit() {
    setLoading(true)
    form.set_form_errors([])

    const discount_term = parseInt(form.discount_term.value, 10)
    const discount_percentage = parseFloat(form.discount_percentage.value)
    const config: CandsConfig = {
      dispute_from_email: selected_email || null,
      dispute_from_name: form.dispute_from_name.value || null,
      vendor_number: form.vendor_number.value || null,
      discount_term: isNaN(discount_term) ? null : discount_term,
      discount_percentage: isNaN(discount_percentage) ? null : discount_percentage,
    }

    const res = await updateIntegration(integration.id, { data: config })
    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to save configuration. Please try again."])
      return
    }
    toast({
      title: "Settings saved",
      description: "Your C&S early pay claim settings have been updated.",
    })
    onSuccess(res.value.data)
  }

  return (
    <Form class="space-y-6" onSubmit={on_submit} alerts={form.form_alerts}>
      <EmailSelector
        emailAccounts={emailAccounts}
        selectedEmail={selected_email}
        onSelect={set_selected_email}
        label="Email Account for UCD Claims"
        description="Claims are emailed to UCDclaims@cswg.com from this account, up to 10 invoices per file."
      />

      <Input
        label="Sender Name"
        type="text"
        name="dispute_from_name"
        placeholder="Your Name"
        bind={form.dispute_from_name}
      />
      <p class="text-gray-500 text-sm -mt-4">Signs the claim emails sent to C&S.</p>

      <Input label="C&S Vendor Number" type="text" name="vendor_number" placeholder="Optional" bind={form.vendor_number} />
      <p class="text-gray-500 text-sm -mt-4">
        Included in the claim email subject and file name so C&S can route it. Optional.
      </p>

      <Input
        label="Discount Rate (%)"
        type="number"
        step="0.01"
        name="discount_percentage"
        placeholder="2"
        bind={form.discount_percentage}
      />
      <p class="text-gray-500 text-sm -mt-4">
        The "2" in "2% 10 Net 30", taken from your C&S contract. Defaults to 2% when left blank.
      </p>

      <Input label="Discount Term (days)" type="number" name="discount_term" placeholder="10" bind={form.discount_term} />
      <p class="text-gray-500 text-sm -mt-4">
        The "10" in "2% 10 Net 30". Together these fill the Terms column of the claim sheet, and the term decides whether
        a check was paid inside the early pay window. Defaults to 10 when left blank.
      </p>

      <Button type="submit" disabled={loading || !selected_email}>
        {loading ? "Saving..." : "Save Configuration"}
      </Button>
    </Form>
  )
}
